import { useCallback, useEffect, useId, useState } from "react";

import { Drawer, Icons, LoadingState, MessageState, useFeedback } from "../../../../../ds";
import { PageFile } from "../../../models/PageFile";
import { PageFilesService } from "../../../services/PageFilesService";
import { useServiceCallPro } from "../../../../../common/services/UseServiceCall";

export interface IMediaPopupProps {
    open: boolean;
    pageId: string;
    spaceId: string;
    /** An upload started from here or from a drop is running (the panel shows a loader and closes when it ends). */
    isUploading?: boolean;
    handleFileUpload: (files: File[]) => void;
    onClose: () => void;
}

const pageFilesSvc = new PageFilesService();

/** Writes to the clipboard; `false` when the browser refuses (insecure context, permission). */
export async function copyTextToClipboard(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
}

/** The markdown a file is inserted with (copied by a tile and after an upload). */
export function fileMarkdown(file: { name: string; url: string }): string {
    return `![${file.name}](${file.url})`;
}

function extensionOf(fileUrl: string): string {
    const last = fileUrl.split("?")[0].split("/").pop() ?? "";
    const dot = last.lastIndexOf(".");
    return dot > 0 ? last.substring(dot + 1).toUpperCase() : "FILE";
}

/**
 * The page's attachments (board "Ark-Editor", right panel): a drop / pick area that uploads through PageFiles, then one
 * tile per file (thumbnail or file-type glyph + name). A tile copies the file's markdown to the clipboard and closes.
 */
export function MediaPopup(props: IMediaPopupProps) {
    const { open, spaceId, pageId, onClose, isUploading } = props;
    const feedback = useFeedback();
    const inputId = useId();
    const [media, setMedia] = useState<PageFile[]>([]);
    const [isOver, setIsOver] = useState(false);
    const [wasUploading, setWasUploading] = useState(false);

    const getFilesCall = useServiceCallPro(pageFilesSvc.getFiles);

    const getFiles = useCallback(() => {
        if (spaceId === undefined || pageId === undefined) return;
        getFilesCall
            .invoke(spaceId, pageId)
            .then((res) => setMedia(res.files))
            .catch((error) => feedback.error(error, "Error!"));
    }, [getFilesCall.invoke, spaceId, pageId, feedback]);

    useEffect(() => {
        if (open) getFiles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, spaceId, pageId]);

    // the old popup closed itself when the upload it showed had finished
    useEffect(() => {
        if (isUploading) setWasUploading(true);
        else if (wasUploading) {
            setWasUploading(false);
            onClose();
        }
    }, [isUploading, wasUploading, onClose]);

    const upload = useCallback(
        (list: FileList | null | undefined) => {
            const files = list ? Array.from(list) : [];
            if (files.length) props.handleFileUpload(files);
        },
        [props]
    );

    const handleFileClick = useCallback(
        (file: PageFile) => {
            void copyTextToClipboard(fileMarkdown(file)).then((ok) => {
                if (ok) feedback.success(`The markdown of “${file.name}” is in the clipboard.`, "Copied");
            });
            onClose();
        },
        [onClose, feedback]
    );

    const busy = isUploading || (getFilesCall.isLoading && media.length === 0);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title="Media"
            lead={
                busy
                    ? undefined
                    : `${media.length} ${media.length === 1 ? "file" : "files"} on this page. Click a file to copy its markdown into the clipboard.`
            }
        >
            {busy ? (
                <LoadingState text={isUploading ? "Uploading…" : "Loading the files…"} />
            ) : (
                <>
                    <label
                        htmlFor={inputId}
                        className={isOver ? "ark-media-drop is-over" : "ark-media-drop"}
                        onDragOver={(ev) => {
                            ev.preventDefault();
                            setIsOver(true);
                        }}
                        onDragLeave={() => setIsOver(false)}
                        onDrop={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            setIsOver(false);
                            upload(ev.dataTransfer?.files);
                        }}
                    >
                        <Icons.upload size={20} />
                        <strong>Click here to upload a file or drop it here</strong>
                        <span className="ark-muted">Images, PDFs and Atlas exports</span>
                        <input
                            id={inputId}
                            type="file"
                            className="ark-media-input"
                            onChange={(ev) => {
                                upload(ev.target.files);
                                ev.target.value = "";
                            }}
                        />
                    </label>

                    {media.length === 0 ? (
                        <MessageState compact illustration={null} title="No files yet" text="Files uploaded to this page are listed here." />
                    ) : (
                        <ul className="ark-media-grid" aria-label="Files of this page">
                            {media.map((file) => (
                                <li key={encodeURI(file.url)}>
                                    <button
                                        type="button"
                                        className="ark-media-tile"
                                        title={`Copy the markdown of ${file.name}`}
                                        onClick={() => handleFileClick(file)}
                                    >
                                        <span className="ark-media-thumb" aria-hidden="true">
                                            {file.thumbnailUrl ? (
                                                // attachments need the bearer token: PageFileResolver loads data-ark-src (OrbitalPlan/18 phase 0)
                                                <img data-ark-src={file.thumbnailUrl} alt="" />
                                            ) : (
                                                <span className="ark-media-kind">
                                                    <Icons.fileText size={22} />
                                                    {extensionOf(file.url)}
                                                </span>
                                            )}
                                        </span>
                                        <span className="ark-media-name">{file.name}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="ark-caption ark-muted">You can also drop a file anywhere on the page; Ark uploads it and copies its markdown.</p>
                </>
            )}
        </Drawer>
    );
}
